import { useState } from 'react';

import type {
  ReportCell,
  ReportVisual,
  ReportVisualMeasure,
  ReportVisualRow,
} from '@/shared/api/resources';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { HelpTip } from '@/shared/ui/FieldLabel';

import {
  TEXTO_PENDENTE,
  alinhaADireita,
  corDaSerie,
  grandezaDaCelula,
  textoDaCelula,
} from './celula';

/**
 * Um visual da pagina do relatorio: cartoes, graficos e matrizes.
 *
 * Os graficos sao desenhados com elementos da propria tela, sem biblioteca: barras e
 * colunas sao proporcoes simples do valor que o servidor mandou. Cada medida tem o
 * proprio grafico (nunca dois eixos no mesmo desenho), toda serie tem legenda e
 * rotulo de valor, e todo grafico tem a tabela ao lado ("Ver tabela"), que e o que o
 * leitor de tela le. As cores sao os tokens da paleta categorica, em ordem fixa.
 */

const visuaisDeCartao = new Set(['cartoes', 'cartao_varias_linhas']);
const visuaisDeTabela = new Set(['matriz', 'tabela']);
const visuaisDeColuna = new Set(['colunas', 'colunas_e_linha']);

export function VisualCard({ visual }: { visual: ReportVisual }) {
  const [verTabela, setVerTabela] = useState(false);
  const ehGrafico =
    !visuaisDeCartao.has(visual.kind) && !visuaisDeTabela.has(visual.kind);

  return (
    <Card className="flex h-full flex-col">
      <CardHeader
        title={visual.title}
        description={visual.onpoint_title}
        action={
          ehGrafico && !visual.pending ? (
            <Button
              size="sm"
              variant="ghost"
              aria-pressed={verTabela}
              onClick={() => setVerTabela((atual) => !atual)}
            >
              {verTabela ? 'Ver grafico' : 'Ver tabela'}
            </Button>
          ) : null
        }
      />
      <CardBody className="flex flex-1 flex-col gap-3">
        {visual.pending ? (
          <Pendencia motivo={visual.pending_reason} />
        ) : visuaisDeCartao.has(visual.kind) ? (
          <Cartoes visual={visual} />
        ) : visuaisDeTabela.has(visual.kind) || verTabela ? (
          <Matriz visual={visual} />
        ) : visual.column && visual.kind === 'barras_empilhadas' ? (
          <BarrasEmpilhadas visual={visual} />
        ) : visuaisDeColuna.has(visual.kind) ? (
          <Colunas visual={visual} />
        ) : (
          <Barras visual={visual} />
        )}
        {visual.note ? <p className="text-2xs text-ink-500">{visual.note}</p> : null}
        {visual.truncated ? (
          <p className="text-2xs text-warning-700">
            Mostrando as primeiras linhas. Use os filtros para recortar.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** Aviso do visual sem definicao: o que falta e por que, no lugar do numero. */
function Pendencia({ motivo }: { motivo: string }) {
  return (
    <div className="rounded-md border border-dashed border-line-strong bg-surface-sunken px-4 py-3">
      <p className="text-xs font-semibold text-ink-700">{TEXTO_PENDENTE}</p>
      <p className="mt-1 text-xs text-ink-500">{motivo}</p>
    </div>
  );
}

function totalDe(visual: ReportVisual): ReportVisualRow | undefined {
  return visual.rows.find((linha) => linha.level === 0);
}

function Cartoes({ visual }: { visual: ReportVisual }) {
  if (visual.levels.length > 0) return <CartoesPorMembro visual={visual} />;
  const total = totalDe(visual);
  return (
    <dl className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {visual.measures.map((medida) => {
        const celula = total?.values[medida.key];
        const pendente = medida.pending || celula?.status === 'pendente';
        return (
          <div
            key={medida.key}
            className="rounded-md border border-line bg-surface-sunken px-3.5 py-3"
          >
            <dt className="flex items-center gap-1 text-2xs font-semibold uppercase tracking-wide text-ink-500">
              {pendente ? (
                <HelpTip label={medida.label} text={medida.pending_reason} />
              ) : (
                medida.label
              )}
            </dt>
            <dd
              className={cn(
                'mt-1 tabular-nums',
                pendente
                  ? 'text-xs italic text-ink-500'
                  : 'text-lg font-semibold text-ink-900',
              )}
            >
              {pendente ? TEXTO_PENDENTE : textoDaCelula(celula) || '0'}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/**
 * Cartoes com corte (os cartoes "por esteira" da Negociacao): um cartao por membro do
 * primeiro nivel, com o nome do membro no titulo. Com mais de uma medida, o titulo
 * leva tambem o rotulo da medida.
 */
function CartoesPorMembro({ visual }: { visual: ReportVisual }) {
  const linhas = linhasDoGrafico(visual);
  if (linhas.length === 0) return <SemMovimento />;
  const varias = visual.measures.length > 1;
  return (
    <dl className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {linhas.flatMap((linha) =>
        visual.measures.map((medida) => {
          const celula = linha.values[medida.key];
          const pendente = medida.pending || celula?.status === 'pendente';
          const titulo = varias
            ? `${rotuloDaLinha(linha)} - ${medida.label}`
            : rotuloDaLinha(linha);
          return (
            <div
              key={`${linha.path.map((item) => item.key).join('/')}-${medida.key}`}
              className="rounded-md border border-line bg-surface-sunken px-3.5 py-3"
            >
              <dt className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
                {titulo}
              </dt>
              <dd
                className={cn(
                  'mt-1 tabular-nums',
                  pendente
                    ? 'text-xs italic text-ink-500'
                    : 'text-lg font-semibold text-ink-900',
                )}
              >
                {pendente ? TEXTO_PENDENTE : textoDaCelula(celula) || '0'}
              </dd>
            </div>
          );
        }),
      )}
    </dl>
  );
}

/** Linhas do primeiro nivel, as que os graficos desenham. */
function linhasDoGrafico(visual: ReportVisual): ReportVisualRow[] {
  return visual.rows.filter((linha) => linha.level === 1);
}

function rotuloDaLinha(linha: ReportVisualRow): string {
  return linha.path.map((item) => item.label || 'Sem valor').join(' / ');
}

/** Medidas que o grafico desenha: as calculadas. A pendente fica na legenda. */
function medidasDesenhaveis(visual: ReportVisual): ReportVisualMeasure[] {
  return visual.measures.filter((medida) => !medida.pending);
}

function PendentesDoGrafico({ visual }: { visual: ReportVisual }) {
  const pendentes = visual.measures.filter((medida) => medida.pending);
  if (pendentes.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1">
      {pendentes.map((medida) => (
        <li key={medida.key} className="text-2xs text-ink-500">
          <span className="font-semibold text-ink-700">{medida.label}:</span>{' '}
          {TEXTO_PENDENTE.toLowerCase()}. {medida.pending_reason}
        </li>
      ))}
    </ul>
  );
}

/**
 * Barras horizontais: uma por linha, um grafico por medida. A barra comeca na linha de
 * base e termina arredondada; o valor fica escrito ao lado, com o texto da tela.
 */
function Barras({ visual }: { visual: ReportVisual }) {
  const linhas = linhasDoGrafico(visual);
  const medidas = medidasDesenhaveis(visual);
  if (linhas.length === 0) return <SemMovimento />;
  return (
    <div className="flex flex-col gap-4">
      {medidas.map((medida) => {
        const maior = Math.max(
          1,
          ...linhas.map((linha) => grandezaDaCelula(linha.values[medida.key]) ?? 0),
        );
        return (
          <figure key={medida.key} className="flex flex-col gap-2">
            {medidas.length > 1 ? (
              <figcaption className="text-2xs font-semibold text-ink-600">
                {medida.label}
              </figcaption>
            ) : null}
            <ul className="flex flex-col gap-1.5" aria-label={medida.label}>
              {linhas.map((linha) => {
                const celula = linha.values[medida.key];
                const valor = grandezaDaCelula(celula) ?? 0;
                const texto = textoDaCelula(celula);
                return (
                  <li
                    key={rotuloDaLinha(linha)}
                    className="group relative flex items-center gap-3"
                    tabIndex={0}
                    aria-label={`${rotuloDaLinha(linha)}: ${texto}`}
                  >
                    <span className="w-40 shrink-0 truncate text-xs text-ink-600">
                      {rotuloDaLinha(linha)}
                    </span>
                    <span className="h-2.5 flex-1 overflow-hidden rounded-r-[4px] bg-ink-100">
                      <span
                        className="block h-full rounded-r-[4px] bg-chart-1"
                        style={{ width: `${Math.max(0, (valor / maior) * 100)}%` }}
                      />
                    </span>
                    <span className="w-28 shrink-0 text-right text-2xs tabular-nums text-ink-700">
                      {texto}
                    </span>
                  </li>
                );
              })}
            </ul>
          </figure>
        );
      })}
      <PendentesDoGrafico visual={visual} />
    </div>
  );
}

/**
 * Colunas no tempo (Mes/Ano): uma coluna por mes, um grafico por medida. O mes sem
 * movimento aparece com zero, e nao some: o servidor manda a serie completa.
 */
function Colunas({ visual }: { visual: ReportVisual }) {
  const linhas = linhasDoGrafico(visual);
  const medidas = medidasDesenhaveis(visual);
  if (linhas.length === 0) return <SemMovimento />;
  return (
    <div className="flex flex-col gap-4">
      {medidas.map((medida, posicao) => {
        const maior = Math.max(
          1,
          ...linhas.map((linha) => grandezaDaCelula(linha.values[medida.key]) ?? 0),
        );
        return (
          <figure key={medida.key} className="flex flex-col gap-2">
            <figcaption className="flex items-center gap-2 text-2xs font-semibold text-ink-600">
              <span
                aria-hidden
                className={cn('inline-block size-2.5 rounded-sm', corDaSerie(posicao))}
              />
              {medida.label}
            </figcaption>
            <div
              className="flex h-36 items-end gap-0.5"
              role="list"
              aria-label={medida.label}
            >
              {linhas.map((linha) => {
                const celula = linha.values[medida.key];
                const valor = grandezaDaCelula(celula) ?? 0;
                const texto = textoDaCelula(celula);
                return (
                  <div
                    key={rotuloDaLinha(linha)}
                    role="listitem"
                    tabIndex={0}
                    aria-label={`${rotuloDaLinha(linha)}: ${texto}`}
                    className="group relative flex h-full flex-1 flex-col items-center justify-end gap-1"
                  >
                    <span className="pointer-events-none absolute -top-1 z-10 hidden -translate-y-full whitespace-nowrap rounded bg-ink-900 px-2 py-1 text-2xs text-white group-hover:block group-focus:block">
                      {rotuloDaLinha(linha)}: {texto}
                    </span>
                    <span
                      className={cn(
                        'w-full max-w-8 rounded-t-[4px]',
                        corDaSerie(posicao),
                      )}
                      style={{
                        height: `${valor > 0 ? Math.max(2, (valor / maior) * 100) : 0}%`,
                      }}
                    />
                    <span className="text-[0.5625rem] text-ink-500">
                      {rotuloDaLinha(linha).slice(0, 2)}
                    </span>
                  </div>
                );
              })}
            </div>
          </figure>
        );
      })}
      <PendentesDoGrafico visual={visual} />
    </div>
  );
}

/**
 * Barras empilhadas: uma barra por linha, um segmento por valor da coluna (situacao,
 * classe), na cor da posicao fixa do valor, com legenda. Dois pixels de fundo separam
 * os segmentos.
 */
function BarrasEmpilhadas({ visual }: { visual: ReportVisual }) {
  const linhas = linhasDoGrafico(visual);
  const medida = medidasDesenhaveis(visual)[0];
  if (!medida || linhas.length === 0) return <SemMovimento />;
  const totais = linhas.map((linha) => grandezaDaCelula(linha.values[medida.key]) ?? 0);
  const maior = Math.max(1, ...totais);
  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-wrap gap-x-4 gap-y-1" aria-label="Legenda">
        {visual.column_values.map((valor, posicao) => (
          <li
            key={valor.key}
            className="flex items-center gap-1.5 text-2xs text-ink-600"
          >
            <span
              aria-hidden
              className={cn('inline-block size-2.5 rounded-sm', corDaSerie(posicao))}
            />
            {valor.label || 'Sem valor'}
          </li>
        ))}
      </ul>
      <ul className="flex flex-col gap-1.5">
        {linhas.map((linha, indice) => (
          <li
            key={rotuloDaLinha(linha)}
            className="flex items-center gap-3"
            tabIndex={0}
            aria-label={`${rotuloDaLinha(linha)}: ${textoDaCelula(linha.values[medida.key])}`}
          >
            <span className="w-40 shrink-0 truncate text-xs text-ink-600">
              {rotuloDaLinha(linha)}
            </span>
            <span className="flex h-2.5 flex-1 gap-0.5 overflow-hidden">
              {visual.column_values.map((valor, posicao) => {
                const parte = grandezaDaCelula(
                  linha.values[`${medida.key}@${valor.key}`],
                );
                if (!parte) return null;
                return (
                  <span
                    key={valor.key}
                    title={`${valor.label}: ${textoDaCelula(linha.values[`${medida.key}@${valor.key}`])}`}
                    className={cn(
                      'block h-full first:rounded-l-none last:rounded-r-[4px]',
                      corDaSerie(posicao),
                    )}
                    style={{ width: `${(parte / maior) * 100}%` }}
                  />
                );
              })}
            </span>
            <span className="w-28 shrink-0 text-right text-2xs tabular-nums text-ink-700">
              {textoDaCelula(linha.values[medida.key]) || String(totais[indice] ?? 0)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SemMovimento() {
  return (
    <p className="text-xs text-ink-500">Sem movimento no periodo e nos filtros.</p>
  );
}

/**
 * Matriz e tabela: os niveis em cascata (o nivel mais fino recuado), os atributos do
 * registro, as medidas e, quando a matriz tem colunas, uma coluna por valor. O total
 * fica na ultima linha. As linhas do segundo nivel em diante abrem pelo botao do pai.
 */
function Matriz({ visual }: { visual: ReportVisual }) {
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const profundo = visual.levels.length > 1;
  const colunas = colunasDaMatriz(visual);

  if (visual.rows.length === 0) return <SemMovimento />;

  const linhasVisiveis = visual.rows.filter((linha) => {
    if (!profundo || linha.level <= 1) return true;
    for (let nivel = 1; nivel < linha.level; nivel += 1) {
      const pai = linha.path
        .slice(0, nivel)
        .map((item) => item.key)
        .join('|');
      if (!abertos.has(pai)) return false;
    }
    return true;
  });

  function alterna(chave: string) {
    setAbertos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(chave)) proximo.delete(chave);
      else proximo.add(chave);
      return proximo;
    });
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <caption className="sr-only">{visual.title}</caption>
        <thead>
          <tr className="border-b border-line bg-surface-sunken">
            <th
              scope="col"
              className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wide text-ink-500"
            >
              {visual.levels.map((nivel) => nivel.label).join(' > ') || 'Total'}
            </th>
            {visual.attributes.map((atributo) => (
              <th
                key={atributo.key}
                scope="col"
                className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wide text-ink-500"
              >
                {atributo.label}
              </th>
            ))}
            {colunas.map((coluna) => (
              <th
                key={coluna.chave}
                scope="col"
                className={cn(
                  'px-3 py-2 text-2xs font-semibold uppercase tracking-wide text-ink-500',
                  alinhaADireita(coluna.medida.kind) ? 'text-right' : 'text-left',
                )}
              >
                {coluna.medida.pending ? (
                  <HelpTip label={coluna.titulo} text={coluna.medida.pending_reason} />
                ) : (
                  coluna.titulo
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhasVisiveis.map((linha) => {
            const chave = linha.path.map((item) => item.key).join('|');
            const ultimo = linha.path[linha.path.length - 1];
            const temFilhos =
              profundo && linha.level > 0 && linha.level < visual.levels.length;
            return (
              <tr
                key={`${linha.level}-${chave}`}
                className={cn(
                  'border-b border-line last:border-b-0',
                  linha.level === 0 && 'bg-surface-sunken font-semibold',
                )}
              >
                <th
                  scope="row"
                  className="px-3 py-2 text-left font-normal text-ink-800"
                  style={{ paddingLeft: `${0.75 + Math.max(0, linha.level - 1)}rem` }}
                >
                  {linha.level === 0 ? (
                    <span className="font-semibold">Total</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      {temFilhos ? (
                        <button
                          type="button"
                          aria-expanded={abertos.has(chave)}
                          aria-label={`${abertos.has(chave) ? 'Recolher' : 'Abrir'} ${ultimo?.label ?? ''}`}
                          onClick={() => alterna(chave)}
                          className="inline-flex size-5 items-center justify-center rounded text-ink-500 hover:bg-ink-100"
                        >
                          {abertos.has(chave) ? '-' : '+'}
                        </button>
                      ) : null}
                      <span className={cn(ultimo?.masked && 'text-ink-500')}>
                        {ultimo?.label || 'Sem valor'}
                      </span>
                    </span>
                  )}
                </th>
                {visual.attributes.map((atributo) => (
                  <td key={atributo.key} className="px-3 py-2 text-ink-700">
                    <TextoDaCelula celula={linha.attributes[atributo.key]} />
                  </td>
                ))}
                {colunas.map((coluna) => (
                  <td
                    key={coluna.chave}
                    className={cn(
                      'px-3 py-2 tabular-nums text-ink-800',
                      alinhaADireita(coluna.medida.kind) && 'text-right',
                    )}
                  >
                    <TextoDaCelula celula={linha.values[coluna.chave]} />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface ColunaDaMatriz {
  chave: string;
  titulo: string;
  medida: ReportVisualMeasure;
}

/** Com coluna (situacao, porte), uma coluna por valor e medida, e o total da linha. */
function colunasDaMatriz(visual: ReportVisual): ColunaDaMatriz[] {
  const colunas: ColunaDaMatriz[] = [];
  for (const medida of visual.measures) {
    if (visual.column && !medida.share_of && !medida.pending) {
      for (const valor of visual.column_values) {
        colunas.push({
          chave: `${medida.key}@${valor.key}`,
          titulo: `${medida.label} - ${valor.label || 'Sem valor'}`,
          medida,
        });
      }
    }
    colunas.push({
      chave: medida.key,
      titulo:
        visual.column && !medida.share_of ? `${medida.label} (total)` : medida.label,
      medida,
    });
  }
  return colunas;
}

export function TextoDaCelula({ celula }: { celula: ReportCell | undefined }) {
  if (!celula || celula.status === 'vazio')
    return <span className="text-ink-400">-</span>;
  if (celula.status === 'pendente') {
    return <span className="italic text-ink-500">{TEXTO_PENDENTE.toLowerCase()}</span>;
  }
  return (
    <span
      className={cn(celula.masked && 'text-ink-500')}
      title={celula.masked ? 'Dado pessoal mascarado' : undefined}
    >
      {textoDaCelula(celula)}
    </span>
  );
}
